import { cn, Container } from '@resolute/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';


import { SIMPLE_NAV_ITEMS } from './nav-menu';
import { useNavMenus } from './use-nav-menus';

import { whatsappHref } from '@/features/contact/contact-details';
import { PANEL_TRANSITION } from '@/lib/motion';


/** Mobile navigation: primary links + collapsible feature sections. */
export function MobileNav({ onNavigate }: { onNavigate: () => void }) {
  const menus = useNavMenus();
  const [expanded, setExpanded] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const itemClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'block rounded-md px-3 py-3 text-base font-medium',
      isActive ? 'bg-muted text-primary' : 'text-foreground',
    );

  return (
    <div className="max-h-[calc(100vh-3.5rem)] overflow-y-auto border-t border-border bg-background lg:hidden">
      <Container className="flex flex-col gap-1 py-4">
        {menus.map((menu) => {
          const open = expanded === menu.id;
          // Groups come from `/filter-options`; until it resolves (or if it
          // failed) there is nothing to expand into, so render a plain link
          // rather than an accordion that opens onto an empty panel.
          if (menu.groups.length === 0) {
            return (
              <NavLink
                key={menu.id}
                to={menu.cta?.to ?? '/'}
                onClick={onNavigate}
                className={itemClass}
              >
                {menu.label}
              </NavLink>
            );
          }
          return (
            <div key={menu.id} className="border-b border-border/60 pb-1 last:border-0">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setExpanded(open ? null : menu.id)}
                className="flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground"
              >
                {menu.label}
                <ChevronDown className={cn('size-5 transition-transform duration-fast', open && 'rotate-180')} aria-hidden="true" />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={reduce ? undefined : { height: 0, opacity: 0 }}
                    animate={reduce ? undefined : { height: 'auto', opacity: 1 }}
                    exit={reduce ? undefined : { height: 0, opacity: 0 }}
                    transition={PANEL_TRANSITION}
                    className="overflow-hidden"
                  >
                    {/* Grouped, not flattened — the taxonomy now comes from
                        `/filter-options` (~39 entries across five groups), and
                        labels legitimately repeat between them ("Plot" and
                        "Office" are both a category AND a property type), so a
                        flat list would read as duplicates with no way to tell
                        which filter a row applies. */}
                    <div className="flex flex-col gap-0.5 pb-2 pl-3">
                      {menu.groups.map((g) => (
                        <div key={g.heading} className="flex flex-col gap-0.5">
                          <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                            {g.heading}
                          </p>
                          {g.items.map((item) => (
                            <NavLink
                              key={item.to}
                              to={item.to}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                cn(
                                  'block rounded-md px-3 py-2.5 text-sm transition-colors duration-base ease-out-expo',
                                  isActive ? 'bg-muted text-primary' : 'text-muted-foreground',
                                )
                              }
                            >
                              {item.label}
                            </NavLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {SIMPLE_NAV_ITEMS.map((item) => {
          if (item.type === 'link') {
            return (
              <NavLink key={item.to} to={item.to} onClick={onNavigate} className={itemClass}>
                {item.label}
              </NavLink>
            );
          }
          const open = expanded === item.id;
          const groups = item.groups ?? (item.items ? [{ items: item.items }] : []);

          return (
            <div key={item.id} className="border-b border-border/60 pb-1 last:border-0">
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setExpanded(open ? null : item.id)}
                className="flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground"
              >
                {item.label}
                <ChevronDown
                  className={cn('size-5 transition-transform duration-fast', open && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    initial={reduce ? undefined : { height: 0, opacity: 0 }}
                    animate={reduce ? undefined : { height: 'auto', opacity: 1 }}
                    exit={reduce ? undefined : { height: 0, opacity: 0 }}
                    transition={PANEL_TRANSITION}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col gap-0.5 pb-2 pl-3">
                      {groups.map((group, idx) => (
                        <div key={idx} className="flex flex-col gap-0.5">
                          {group.heading && (
                            <p className="px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                              {group.heading}
                            </p>
                          )}
                          {group.items.map((link) => (
                            <NavLink
                              key={link.to}
                              to={link.to}
                              onClick={onNavigate}
                              className={({ isActive }) =>
                                cn(
                                  'block rounded-md px-3 py-2 text-sm transition-colors duration-base ease-out-expo',
                                  isActive ? 'bg-muted text-primary' : 'text-muted-foreground',
                                )
                              }
                            >
                              {link.label}
                            </NavLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        <div className="mt-3 px-3">
          {/* Same WhatsApp deep link as the desktop header button. `onNavigate`
              still closes the menu, since the link opens in a new tab and would
              otherwise leave it open behind. */}
          <a
            href={whatsappHref()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onNavigate}
            className="text-sm font-medium text-primary"
          >
            Talk to an advisor →
          </a>
        </div>
      </Container>
    </div>
  );
}
